
import React from 'react';
import { Typography, Grid, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InfoGrid, ReportFooterTimestamp } from '../../../utils/reportUtils';

type TStimulationFunc = (key: string) => string;
type FollicularDetails = {
  date: string;
  time: string;
  lmpDate: string;
  cycleDay: string;
  endometriumThickness?: string;
  ri?: string;
  psv?: string;
  endometriumMorphology?: string;
  cystLeftOvary?: string;
  cystRightOvary?: string;
  remark?: string;
};

interface FollicularScanReportProps {
  tStimulation: TStimulationFunc;
  follicularDetails: FollicularDetails;
  folliclesData: {
    [key: number]: {
      leftSize: string;
      rightSize: string;
      leftFollicles: string;
      rightFollicles: string;
    };
  };
  rows: number[];
}

const FollicularScanReport: React.FC<FollicularScanReportProps> = ({
  tStimulation,
  follicularDetails,
  folliclesData,
  rows,
}) => {
  const { t: tGeneral } = useTranslation('general');
  const follicularDetailsGridData: string[][] = [
    [
      tStimulation('date'),
      follicularDetails?.date || '-',
      tStimulation('time'),
      follicularDetails?.time || '-'
    ],
    [
      tStimulation('lmpDate'),
      follicularDetails?.lmpDate || '-',
      tStimulation('cycleDay'),
      follicularDetails?.cycleDay || '-'
    ],
    [
      tStimulation('endometriumThickness'),
      follicularDetails?.endometriumThickness || '-',
      tStimulation('ri'),
      follicularDetails?.ri || '-'
    ],
    [
      tStimulation('psv'),
      follicularDetails?.psv || '-',
      tStimulation('endometriumMorphology'),
      follicularDetails?.endometriumMorphology || '-'
    ],
    [
      tStimulation('cystLeftOvary'),
      follicularDetails?.cystLeftOvary || '-',
      tStimulation('cystRightOvary'),
      follicularDetails?.cystRightOvary || '-'
    ],
    [
      tStimulation('remark'),
      follicularDetails?.remark || '-',
      '',
      ''
    ]
  ];

  return (
    <Box sx={{ p: '0px 35px 35px 35px', width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#444' }}>{tGeneral('follicularScan_Details')}</Typography>
      {/* Top Details */}
      <Box sx={{ mb: 1 }}>
        <InfoGrid data={follicularDetailsGridData} />
      </Box>

      <Box sx={{ borderBottom: '1px solid #ddd', my: 3 }} />

      {/* Left & Right Ovary Headers */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="h6" align="center">{tStimulation('leftOvary')}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6" align="center">{tStimulation('rightOvary')}</Typography>
        </Grid>
      </Grid>

      {/* Table Header Row */}
      <Grid container sx={{ mt: 1, backgroundColor: '#f8f8f8', py: 1 }}>
        <Grid item xs={3}>
          <Typography variant="subtitle2" align="center">{tStimulation('follicularSize')}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="subtitle2" align="center">{tStimulation('noOfFollicles')}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="subtitle2" align="center">{tStimulation('follicularSize')}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="subtitle2" align="center">{tStimulation('noOfFollicles')}</Typography>
        </Grid>
      </Grid>

      {/* Data Rows */}
      {rows.map((rowId: number) => (
        <Grid container key={rowId} sx={{ borderBottom: '1px solid #eee', py: 0.5 }}>
          <Grid item xs={3}>
            <Typography variant="body2" align="center">
              {folliclesData[rowId]?.leftSize ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" align="center">
              {folliclesData[rowId]?.leftFollicles ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" align="center">
              {folliclesData[rowId]?.rightSize ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" align="center">
              {folliclesData[rowId]?.rightFollicles ?? '-'}
            </Typography>
          </Grid>
        </Grid>
      ))}

      <Box sx={{ borderBottom: '1px solid #ddd', my: 3 }} />

   
      <ReportFooterTimestamp />
      
    </Box >

  );
};

export default FollicularScanReport;